// src/hooks/useNotifications.js
//
// Pass 85.1B.1 (§6/§7/§19) — the STAFF notification listener, and ONLY the staff one. It no longer runs a UID-global
// query: it subscribes ONLY when explicitly `enabled` AND given the authorised `organizationId`, and the query is shaped
// `where userId == uid AND organizationId == <authorised org>` so the hardened rules can prove every returned row
// (a staff recipient reads their org notifications via the staff branch). With no org / not enabled it starts NO
// Firestore listener and returns [] — so an unresolved or non-staff surface performs ZERO reads here. A canonical
// STUDENT never uses this hook; student comms flow through useStudentComms (participant + org scoped).

import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { normalizeNotification } from '../utils/notifications';

export function useNotifications(options = {}) {
  const { currentUser } = useAuth() || {};
  const { enabled = false, organizationId = '', unreadOnly = false } = options;
  const uid = currentUser?.uid || '';
  // Fail closed: subscribe ONLY for an explicitly enabled, org-scoped staff read.
  const active = !!(enabled && uid && organizationId);
  const scopeKey = active ? `${uid}::${organizationId}::${unreadOnly ? 'u' : 'a'}` : '';
  const [snap, setSnap] = useState({ scopeKey: '', items: [] });

  useEffect(() => {
    setSnap({ scopeKey, items: [] });
    if (!scopeKey) return undefined;
    // Pass 85.1B.3 — the staff read is AUDIENCE-scoped: `audience == 'organization_staff'` so the query matches ONLY the
    // operational notifications the rules authorise. A commercial/invitee/account notification addressed to the SAME uid is
    // never matched (nor readable), so the query result is all-readable and a dual-relationship staff account is safe (§19).
    const constraints = [
      where('userId', '==', uid),
      where('organizationId', '==', organizationId),
      where('audience', '==', 'organization_staff'),
    ];
    if (unreadOnly) constraints.push(where('read', '==', false));
    // No orderBy → callers sort client-side (newest-first below).
    const q = query(collection(db, 'notifications'), ...constraints);
    const unsub = onSnapshot(
      q,
      (s) => setSnap({ scopeKey, items: s.docs.map((d) => normalizeNotification({ id: d.id, ...d.data() })) }),
      () => setSnap({ scopeKey, items: [] }),
    );
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [scopeKey, uid, organizationId, unreadOnly]);

  // Synchronous stale-scope suppression at render (mirrors useStudentComms): a scope change never shows prior rows.
  return useMemo(() => {
    if (snap.scopeKey !== scopeKey) return [];
    const ts = (v) => (v?.seconds || v?._seconds || (v?.toDate ? v.toDate().getTime() / 1000 : 0) || 0);
    return [...snap.items].sort((a, b) => ts(b.createdAt) - ts(a.createdAt));
  }, [snap, scopeKey]);
}
